import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { CampaignManager } from "../../../core/storage/CampaignManager";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { Campaign } from "../../../ducks/campaign";
import { StoreSearchType } from "../../../ducks/store";
import { CouponHook } from "../../../hooks/useCoupon";
import { useMenu } from "../../../hooks/useMenu";
import { useNavigation } from "../../../hooks/useNavigation";
import {
  convertToISO8601,
  formatDateTime,
  getDatePlusMinutes,
  isDateTimeBefore,
} from "../../../utils/date";

export const useMenuList = (useCoupon: CouponHook) => {
  const { navigateToTopPage, navigateToPage } = useNavigation();
  const { storeCode, date } = useParams<{ storeCode: string; date: string }>();
  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  // 在庫販売モードかどうか
  const isStockItemMode = CampaignManager.isStockItemMode();

  const {
    store,
    menuMapById,
    recommendedMenus,
    limitedMenus,
    stockGroups,
    searchMenus,
    searchStockMenus,
    categoriesToDisplay,
    handleSetFavoriteStore,
    handleSetFavoriteMenu,
  } = useMenu(useCoupon, storeCode, date);

  const {
    appliedCoupons,
    selectedAppCoupons,
    applyCoupon,
    handleRemoveCoupon,
    getCouponName,
  } = useCoupon;

  useEffect(() => {
    if (!store || !date) return;

    if (isStockItemMode && campaign?.id) {
      // 在庫販売モード：在庫APIを使用
      searchStockMenus(store.id, campaign.id, date);
    } else {
      // 通常・早割モード
      searchMenus(store.id, date);
    }
  }, [store, date, appliedCoupons, selectedAppCoupons]);

  useEffect(() => {
    if (!store || !date) return;

    const shortestPickupDate = getDatePlusMinutes(
      new Date(),
      store.shortestTimeUntilPickup,
    );

    if (isDateTimeBefore(date, shortestPickupDate)) {
      navigateToPage(
        PageRoutes.MENU_LIST.replace(":storeCode", store.storeCode).replace(
          ":date",
          convertToISO8601(shortestPickupDate),
        ),
      );
    }
  }, [store, date]);

  const pickupDateText = useMemo(() => {
    if (!date) return "";

    return formatDateTime(new Date(date), true);
  }, [date]);

  const handleOnClickChangePickupDate = useCallback(() => {
    if (!storeCode) return;

    navigateToPage(
      PageRoutes.SHOP_SEARCH.replace(
        ":type",
        StoreSearchType.StoreCode.valueOf(),
      ).replace(":query", storeCode),
      undefined,
      { selectedDate: date },
    );
  }, [storeCode]);

  const handleChangeStore = useCallback(() => {
    if (!!campaign) {
      navigateToTopPage();
    } else {
      navigateToPage(PageRoutes.HOME, false, { isRegular: true });
    }
  }, [campaign]);

  return {
    store,
    date,
    recommendedMenus,
    limitedMenus,
    stockGroups, // 追加
    isStockItemMode, // 追加
    categoriesToDisplay,
    pickupDateText,
    handleSetFavoriteStore,
    navigateToTopPage,
    handleOnClickChangePickupDate,
    handleSetFavoriteMenu,
    menuMapById,
    appliedCoupons,
    applyCoupon,
    handleRemoveCoupon,
    getCouponName,
    handleChangeStore,
  };
};
